"use client";

import {
  useRef,
  useState,
  type MouseEventHandler,
  type Ref,
  type ChangeEventHandler,
  useEffect,
  type DragEvent,
  type FC,
} from "react";
import { isObject, mergeClasses } from "@huub-web/utils";
import { useTranslation } from "react-i18next";

import type { ValidatableProps } from "../../props";
import { Text } from "../Text";
import { BaseInputField, type BaseInputFieldProps } from "../BaseInputField";
import type { UploadedDocument } from "../../types/UploadedDocument";
import { mergeRefs } from "../../utils/mergeRefs";
import { Icon } from "../Icon";
import { Chip } from "../Chip";
import { Popup, type PopupProps } from "../Popup";
import { useFormValidation } from "../../hooks/useFormValidation";
import type { FileUploadFieldChangeHandler } from "../FileUploadField";
import { useClickOutside } from "../../hooks/useClickOutside";
import { SelectList, type SelectListOption } from "../SelectList";
import { ProgressBar } from "../ProgressBar";
import { useDebug } from "../../hooks/useDebug";
import { InputAddOn } from "../InputAddOn";

import { iconStyle } from "./DocumentUploadField.css";
import { inputStyle } from "../BaseInputField/BaseInputField.css";

export type DocumentUploadFieldProps = Omit<
  BaseInputFieldProps,
  "defaultValue" | "multiLine" | "value"
> &
  ValidatableProps & {
    value?: UploadedDocument | null;
    popup?: Pick<PopupProps, "placement" | "minWidth" | "maxWidth" | "width">;
    inputRef?: Ref<HTMLInputElement>;
    placeholder?: string;
    multiple?: boolean;
    allowedTypes?: string[];
    loading?: boolean;
    onChange?: FileUploadFieldChangeHandler;
    onOutsideClick?: () => void;
    onDelete?: (id: string) => void;
    onDownload?: (url?: string) => void;
  };

export type DocumentUploadFieldAction = "replace" | "delete" | "upload";

/**
 * A card that allows the user to upload files.
 * There is an option to upload multiple files, but this component only supports one file at a time.
 * It does provide all files in the onChange event. (E.g. this can be used in a higher order component
 * to provide a list of files.)
 */
export const DocumentUploadField: FC<DocumentUploadFieldProps> = ({
  id: idProp,
  error: errorProp,
  disabled: disabledProp,
  placeholder,
  inputRef,
  value = null,
  popup,
  variant = "default",
  autoFocus = false,
  loading = false,
  validation,
  multiple = false,
  leading = <InputAddOn icon="documentUpload" variant="leading" />,
  name,
  allowedTypes = [
    "application/pdf",
    "application/msword",
    "image/png",
    "image/jpeg",
    "image/gif",
    "image/webp",
  ],
  onClick,
  onChange,
  onOutsideClick,
  onDelete,
  onDownload,
  ref,
  ...props
}) => {
  const { t } = useTranslation();
  const { log } = useDebug();
  const internalInputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);

  const [isPopupActive, setIsPopupActive] = useState(autoFocus);

  const { id, disabled, error } = useFormValidation<UploadedDocument | null>(
    value,
    {
      id: idProp,
      error: errorProp,
      disabled: disabledProp,
      validation,
      name,
      ref: internalInputRef,
    },
  );

  useClickOutside([popupRef, fieldRef], () => {
    closePopup();
    onOutsideClick?.();
  });

  const filesRef = useRef<File[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const uploadActionRef = useRef<DocumentUploadFieldAction>("upload");

  const hasFile = isObject(value) || files.length > 0;

  const handleClick: MouseEventHandler<HTMLInputElement> = (e) => {
    uploadActionRef.current = "upload";
    if (hasFile) {
      openPopup();
    } else {
      // No need to programmatically click the input - it's already being clicked
      // naturally since it covers the field with fullProxy styling
      onClick?.(e);
    }
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    log(
      "cyan",
      "DocumentUploadField",
      "handleChange",
      uploadActionRef.current,
      e.target.files,
    );

    onChange?.(e.target.files, e);

    const files =
      e.target.files && e.target.files.length > 0
        ? Array.from(e.target.files)
        : [];

    setFiles(files);
    filesRef.current = files;
  };

  const handleGenericDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) {
      return;
    }

    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles || droppedFiles.length === 0) {
      return;
    }

    // Filter files by allowed types
    const validFiles = Array.from(droppedFiles).filter((file) =>
      allowedTypes.includes(file.type),
    );

    if (validFiles.length === 0) {
      log("red", "DocumentUploadField", "handleDrop", "No valid files in drop");

      return;
    }

    log(
      "cyan",
      "DocumentUploadField",
      "handleDrop",
      uploadActionRef.current,
      validFiles,
    );

    // Create a synthetic FileList
    const dataTransfer = new DataTransfer();
    validFiles.forEach((file) => {
      dataTransfer.items.add(file);
    });

    onChange?.(dataTransfer.files);

    setFiles(multiple ? validFiles : [validFiles[0]]);
    filesRef.current = multiple ? validFiles : [validFiles[0]];
  };

  const openPopup = () => {
    if (disabled) {
      return;
    }
    setIsPopupActive(true);
  };

  const handleClose = () => {
    closePopup();
  };

  const closePopup = () => {
    setIsPopupActive(false);
  };

  const options: SelectListOption[] = (() => {
    const options: SelectListOption[] = [
      {
        leading: <Icon icon="reload" size="s" />,
        label: t("fileUpload.replaceFile"),
        value: "replace",
      },
    ];

    if (onDownload) {
      options.push({
        leading: <Icon icon="link" size="s" />,
        label: t("fileUpload.downloadFile"),
        value: "download",
      });
    }

    if (onDelete) {
      options.push({
        leading: <Icon icon="delete" size="s" />,
        label: t("fileUpload.deleteFile"),
        value: "delete",
        variant: "error",
      });
    }

    if (multiple) {
      options.push({
        leading: <Icon icon="documentUpload" size="s" />,
        label: t("fileUpload.uploadAdditionalFile"),
        value: "upload",
        ruler: true,
      });
    }

    return options;
  })();

  useEffect(() => {
    if (!value && files.length !== 0) {
      log(
        "cyan",
        "DocumentUploadField",
        "Empty value detected, clearing files",
      );
      setFiles([]);
    }
  }, [value, files]);

  return (
    <>
      <Popup
        ref={popupRef}
        active={isPopupActive}
        onClose={handleClose}
        content={
          <SelectList
            options={options}
            onSelect={(option) => {
              switch (option.value) {
                case "delete":
                  if (value?.id) {
                    onDelete?.(value?.id);
                  }
                  uploadActionRef.current = "delete";
                  break;

                case "replace":
                  internalInputRef.current?.click();
                  uploadActionRef.current = "replace";
                  break;

                case "download":
                  onDownload?.(value?.url);
                  break;

                case "upload":
                  internalInputRef.current?.click();
                  break;
              }

              closePopup();
            }}
          />
        }
        variant={variant === "toggle" ? "toggle" : undefined}
        applyActive
        escape
        size="minimal"
        {...popup}
        placement={popup?.placement ?? "bottomRight"}
      >
        <BaseInputField
          id={id}
          altRef={mergeRefs(ref, fieldRef)}
          onClick={handleClick}
          onDragEnter={handleGenericDrag}
          onDragLeave={handleGenericDrag}
          onDragOver={handleGenericDrag}
          onDrop={handleDrop}
          width="stretch"
          disabled={disabled}
          variant={variant}
          error={error}
          overflowHidden
          leading={leading}
          trailingLast={
            variant === "default" || variant === "opaque" ? (
              <Icon
                icon="chevronRight"
                size="s2"
                className={iconStyle({
                  state: hasFile
                    ? isPopupActive
                      ? "open"
                      : "closed"
                    : "default",
                })}
              />
            ) : undefined
          }
          trailing={
            <>
              {value?.status === "uploaded" && (
                <Chip
                  variant="tertiary"
                  leading={<Icon icon="documentCheck" size="s" />}
                >
                  {t("fileUpload.submitted")}
                </Chip>
              )}
              {value?.status === "approved" && (
                <Chip
                  variant="success"
                  leading={<Icon icon="check" size="s" />}
                >
                  {t("fileUpload.approved")}
                </Chip>
              )}
              {value?.status === "declined" && (
                <Chip
                  variant="error"
                  leading={<Icon icon="alertFilled" size="s" />}
                >
                  {t("fileUpload.declined")}
                </Chip>
              )}
              {value?.status === "pending" && (
                <Chip
                  variant="warning"
                  leading={<Icon icon="waiting" size="s" />}
                >
                  {t("fileUpload.pending")}
                </Chip>
              )}
            </>
          }
          cursor="default"
          proxy={
            <>
              {loading && (
                <ProgressBar variant="infinite" position="absolute" />
              )}
              <input
                id={id}
                ref={mergeRefs(inputRef, internalInputRef)}
                className={mergeClasses(
                  inputStyle({
                    proxy: hasFile,
                    fullProxy: !hasFile,
                    disabled,
                  }),
                )}
                disabled={disabled}
                type="file"
                accept={allowedTypes.join(",")}
                onChange={handleChange}
                autoComplete="off"
                data-1p-ignore
                multiple={multiple}
              />
            </>
          }
          placeholder={hasFile ? undefined : placeholder}
          {...props}
        >
          {hasFile ? (
            <Text ellipsis>{value ? value.name : files?.[0]?.name}</Text>
          ) : null}
        </BaseInputField>
      </Popup>

      {/* <pre>{JSON.stringify(value, null, 2)}</pre> */}
    </>
  );
};
